import {
  Box,
  Button,
  Checkbox,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Input,
  Select,
  SpaceBetween,
} from '@cloudscape-design/components';
import {
  LineItemTypes,
  OrderStatus,
  OrderTypes,
  StepTypes,
} from '@parallel-fluidics/shared/constants';
import { formatDate } from '@parallel-fluidics/shared/shipping';
import { useNotifications } from 'features/notifications';
import OrganizationSelect from 'features/organization/components/organizationSelect';
import UserSelectByOrg from 'features/user/components/userSelectByOrg';
import {
  collection,
  doc,
  getFirestore,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import {
  FieldArray,
  Form,
  Formik,
} from 'formik';
import PropTypes from 'prop-types';
import { useUserData } from 'providers/userDataProvider';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import {
  formatCurrency,
  getShippingEstimateText,
} from 'utils';
import * as Yup from 'yup';

import FileUploader from '../../FileUploader';
import AddressDisplay from '../package/addressDisplay';
import LineItemsTable from './lineItemsTable';
import PackageTable from './packageTable';

const orderValidationSchema = Yup.object().shape({
  uid: Yup.string().required('UID is required'), // order creator user ID
  organization: Yup.string().required('Organization is required'),
  name: Yup.string(),
  orderDate: Yup.string()
    .required('Order Date is required')
    .matches(/^\d{4}-\d{2}-\d{2}$/, 'Order Date must be in the format YYYY-MM-DD'),
  subtotal: Yup.string().when('isInternal', {
    is: false,
    then: Yup.string()
      .required('Subtotal is required')
      .matches(/^\$\d{1,3}(,\d{3})*(\.\d{2})?$/, 'Subtotal must be in the format $0.00'),
  }),
  purchaseOrder: Yup.array().of(
    Yup.object().shape({
      downloadURL: Yup.string().url('Must be a valid URL').required('Download URL is required'),
      lastModifiedTS: Yup.number().required('Last Modified Timestamp is required'),
      name: Yup.string().required('Name is required'),
      ref: Yup.string().required('Reference URL is required'),
      type: Yup.string().required('Type is required'),
    }),
  ),
  shipments: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().when('isInternal', {
        is: false,
        then: Yup.string().required('Shipment Name is required'),
      }),
      targetShipDate: Yup.string()
        .required('Target Ship Date is required')
        .matches(/^\d{4}-\d{2}-\d{2}$/, 'Target Ship Date must be in the format YYYY-MM-DD'),
      addressData: Yup.object().when('isInternal', {
        is: false,
        then: Yup.object().shape({
          name: Yup.string().required('Name is required'),
          company: Yup.string(),
          phone: Yup.string().required('Phone is required'),
          street1: Yup.string().required('Address is required'),
          street2: Yup.string(),
          city: Yup.string().required('City is required'),
          state: Yup.string().required('State is required'),
          zip: Yup.string().required('Zip Code is required'),
          country: Yup.string().required('Country is required'),
        }).required('Address is required'),
      }),
      shippingMethod: Yup.string(),
      isShipped: Yup.boolean().required('Shipped is required'),
      lineItems: Yup.array().of(
        Yup.object().shape({
          type: Yup.string().oneOf([
            LineItemTypes.ON_DEMAND,
            LineItemTypes.PRODUCT,
          ]).default(LineItemTypes.ON_DEMAND),
          // on_demand fields - only required when type === 'on_demand'
          projectId: Yup.string().when('type', {
            is: LineItemTypes.ON_DEMAND,
            then: Yup.string().required('Project ID is required'),
            otherwise: Yup.string().nullable(),
          }),
          pPartId: Yup.string()
            .when('type', {
              is: LineItemTypes.ON_DEMAND,
              then: Yup.string()
                .required('Project Part ID is required')
                .test(
                  'match-last-step-pPartId',
                  "Project Part ID must match the last step's pPartId",
                  (value, ctx) => {
                    const { steps } = ctx.parent;
                    if (!Array.isArray(steps) || steps.length === 0) return true;
                    let lastRelevantPPartId = null;
                    for (let i = steps.length - 1; i >= 0; i -= 1) {
                      const step = steps[i];
                      if (step.type !== StepTypes.INSPECTION) {
                        lastRelevantPPartId = step.pPartId;
                        break;
                      }
                    }
                    if (!lastRelevantPPartId) return true;
                    return value === lastRelevantPPartId;
                  },
                ),
              otherwise: Yup.string().nullable(),
            }),
          bonding: Yup.string().when('type', {
            is: LineItemTypes.ON_DEMAND,
            then: Yup.string().required('Bonding type is required'),
            otherwise: Yup.string().nullable(),
          }),
          steps: Yup.array().when('type', {
            is: LineItemTypes.ON_DEMAND,
            then: Yup.array().of(
              Yup.object().shape({
                type: Yup.string().required('Step type is required'),
                pPartId: Yup.string().when('type', {
                  is: StepTypes.INSPECTION,
                  then: Yup.string().nullable(),
                  otherwise: Yup.string().required('Part ID is required'),
                }),
                goal: Yup.number().positive().typeError('Goal must be a positive number').required('Step goal is required'),
              }),
            ).min(1, 'At least one step is required'),
            otherwise: Yup.array().nullable(),
          }),
          // product fields - only required when type === 'product'
          productDocId: Yup.string().when('type', {
            is: LineItemTypes.PRODUCT,
            then: Yup.string().required('SKU / Product is required'),
            otherwise: Yup.string().nullable(),
          }),
          productVersionId: Yup.string().when('type', {
            is: LineItemTypes.PRODUCT,
            then: Yup.string().required('Version is required'),
            otherwise: Yup.string().nullable(),
          }),
          // shared fields
          quantity: Yup.number().positive().typeError('Quantity must be a positive number').required('Quantity is required'),
          notes: Yup.string(),
          name: Yup.string(),
          desc: Yup.string(),
          kickoffDoc: Yup.string().url('Must be a valid URL'),
          reorderSuggestionId: Yup.string().nullable(),
        }),
      ).min(1, 'At least one line item is required'),
      amount: Yup.string().when('isInternal', {
        is: false,
        then: Yup.string()
          .required('Amount is required')
          .matches(/^\$\d{1,3}(,\d{3})*(\.\d{2})?$/, 'Amount must be in the format $0.00'),
      }),
    }),
  ).min(1, 'At least one shipment is required'),
  status: Yup.object().shape({
    label: Yup.string().required('Status label is required'),
    value: Yup.string().required('Status value is required'),
  })
    .required('Status is required')
    .test(
      'is-shipped-when-completed',
      'All shipments must be marked as `Shipped` when status is Completed',
      (status, context) => {
        const { shipments } = context.parent;
        if (status?.value === OrderStatus.COMPLETED) {
          return shipments.every((shipment) => shipment.isShipped === true);
        }
        return true;
      },
    ),
  stripeId: Yup.string(),
});

const defaultLineItem = {
  type: LineItemTypes.ON_DEMAND,
  projectId: '',
  pPartId: '',
  quantity: null,
  bonding: '',
  notes: '',
  name: '',
  desc: '',
  kickoffDoc: '',
  steps: Object.values(StepTypes).map((type) => ({
    type,
    goal: null,
  })),
};

const defaultShipment = {
  name: '',
  addressData: {
    name: '',
    company: '',
    phone: '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
  },
  targetShipDate: '',
  shippingMethod: '',
  isShipped: false,
  lineItems: [
    { ...defaultLineItem },
  ],
  amount: '',
};

function normalizeInternalOrderLineItem(lineItem) {
  const normalizedLineItem = {
    ...lineItem,
    type: LineItemTypes.ON_DEMAND,
  };

  delete normalizedLineItem.productDocId;
  delete normalizedLineItem.productVersionId;
  delete normalizedLineItem.sku;
  delete normalizedLineItem.unitPrice;
  delete normalizedLineItem.leadtimeDays;

  return normalizedLineItem;
}

export default function ManageOrder({
  initData = {
    orderDate: formatDate(new Date()),
    status: OrderStatus.NOT_STARTED,
    purchaseOrder: [],
    shipments: [{ ...defaultShipment }],
  },
  isEditing = false,
}) {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const { addNotification } = useNotifications();
  const [searchParams] = useSearchParams();
  const isInternal = useMemo(() => initData.isInternal || searchParams.get('type') === OrderTypes.INTERNAL, [initData.isInternal, searchParams]);
  const { user } = useUserData();

  const [selectedOrg, setSelectedOrg] = useState(null);

  // Scroll to element if URL has a hash (e.g., #item_xxx)
  useEffect(() => {
    if (!hash) return;
    const id = decodeURIComponent(hash.slice(1));
    const el = document.getElementById(id) || document.querySelector(hash);
    if (!el) return;

    // scroll to the element
    requestAnimationFrame(() => {
      const offset = 25;
      const y = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    });
  }, [hash]);

  // Add a new document with a generated id
  const newOrderRef = useMemo(() => {
    if (isEditing) {
      return doc(getFirestore(), 'orders', initData.id);
    }
    return doc(collection(getFirestore(), 'orders'));
  }, [initData.id, isEditing]);

  const initialValues = useMemo(() => ({
    uid: '',
    creator: user?.id,
    organization: user?.organization,
    isInternal,
    ...initData,
    status: { label: initData.status, value: initData.status },
  }), [initData, user, isInternal]);

  const handleSubmitOrder = useCallback(async (values) => {
    const normalizedValues = values.isInternal
      ? {
        ...values,
        shipments: values.shipments.map((shipment) => ({
          ...shipment,
          lineItems: shipment.lineItems.map(normalizeInternalOrderLineItem),
        })),
      }
      : values;
    // eslint-disable-next-line no-unused-vars
    const { id, ...castValue } = orderValidationSchema.cast(normalizedValues);
    const orderId = newOrderRef.id;

    const processedShipments = castValue.shipments.map((shipment, shipIdx) => {
      const lineItems = shipment.lineItems.map((lineItem, lineItemIdx) => {
        const lineItemType = lineItem.type || LineItemTypes.ON_DEMAND;
        const lineItemId = lineItemType === LineItemTypes.PRODUCT
          ? `${lineItem.sku}_${orderId}_${shipIdx}_${lineItemIdx}`
          : `${lineItem.projectId}_${orderId}_${shipIdx}_${lineItemIdx}`;

        return {
          ...lineItem,
          orderId,
          id: lineItemId,
        };
      });
      return { ...shipment, lineItems };
    });

    try {
      await setDoc(newOrderRef, {
        ...castValue,
        ...(isEditing ? {} : { createdAt: serverTimestamp() }),
        status: castValue.status?.value || OrderStatus.NOT_STARTED,
        shipments: processedShipments,
        hasShippedItems: castValue.shipments.some((shipment) => shipment.isShipped),
      });

      // redirect to the order detail page
      navigate(`/orders?${searchParams.toString()}`);
    } catch (error) {
      addNotification({
        type: 'error',
        dismissible: true,
        content: `Failed to create order. ${error.message}`,
        id: 'create-order-error',
      });
    }
  }, [addNotification, isEditing, navigate, newOrderRef, searchParams]);

  const getLastSixUppercase = useCallback((str) => {
    if (str && typeof str === 'string') {
      return str.trim().slice(-6).trim().toUpperCase();
    }
    return ''; // or handle it as needed if the string doesn't exist
  }, []);

  const handleNameChange = useCallback((value, values, setFieldValue) => {
    const lastSixUppercase = getLastSixUppercase(value);
    setFieldValue('name', value);
    values.shipments.forEach((shipment, index) => {
      setFieldValue(`shipments[${index}].name`, `${lastSixUppercase}.${index + 1}`);
    });
  }, [getLastSixUppercase]);

  const handleNewLineItem = useCallback(({ shipmentIndex, setFieldValue, currentLineItems }) => {
    setFieldValue(`shipments[${shipmentIndex}].lineItems`, [
      ...currentLineItems,
      defaultLineItem,
    ]);
  }, []);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={orderValidationSchema}
      validateOnChange={false}
      validateOnBlur={false}
      enableReinitialize
      onSubmit={(values, { setSubmitting }) => {
        handleSubmitOrder(values);
        setSubmitting(false);
      }}
    >
      {({
        values,
        errors,
        dirty,
        setFieldValue,
        isSubmitting,
      }) => (
        <Form>
          <SpaceBetween direction="vertical" size="s">
            {/* Order Basic Info */}
            <Container>
              <ColumnLayout columns={2}>
                <OrganizationSelect
                  errorText={errors.organization}
                  selectedOrgId={values.organization}
                  disabled={isEditing}
                  onOrgChange={(org, isManualChange) => {
                    setFieldValue('organization', org.id);
                    if (isManualChange) setFieldValue('uid', ''); // reset selected user when org changes
                    setSelectedOrg(org);
                  }}
                />

                <UserSelectByOrg
                  label="Order Requested By (User ID)"
                  errorText={errors.uid}
                  organization={selectedOrg}
                  selectedUserId={values.uid}
                  onUserChange={(userData) => setFieldValue('uid', userData?.id || '')}
                />

                <FormField
                  label="Order Date"
                  errorText={errors.orderDate}
                >
                  <Input
                    name="orderDate"
                    value={values.orderDate}
                    onChange={({ detail }) => { setFieldValue('orderDate', detail.value); }}
                  />
                </FormField>

                <FormField
                  label="Status"
                  errorText={errors.status}
                >
                  <Select
                    selectedOption={values.status}
                    onChange={({ detail }) => { setFieldValue('status', detail.selectedOption); }}
                    options={Object.values(OrderStatus).map((status) => (
                      { label: status, value: status }
                    ))}
                  />
                </FormField>

                {(!!values.subtotal || !isInternal) && (
                  <FormField
                    label={(
                      <span>
                        Subtotal
                        {' '}
                        <i>- excluding shipping and tax</i>
                      </span>
                    )}
                    constraintText={
                      getShippingEstimateText(values.shipCharged, values.packageCount)
                    }
                    errorText={errors.subtotal}
                  >
                    <Input
                      name="subtotal"
                      value={values.subtotal}
                      onChange={({ detail }) => {
                        setFieldValue('subtotal', detail.value);
                      }}
                      onBlur={() => { setFieldValue('subtotal', formatCurrency(values.subtotal)); }}
                    />
                  </FormField>
                )}

                {(!!values.stripeId) && (
                  <FormField
                    label="Stripe Id"
                    errorText={errors.stripeId}
                  >
                    <Input
                      name="stripeId"
                      value={values.stripeId}
                      onChange={({ detail }) => {
                        setFieldValue('stripeId', detail.value);
                      }}
                    />
                  </FormField>
                )}

                <FormField
                  label="Name (Optional)"
                  errorText={errors.name}
                >
                  <Input
                    name="name"
                    value={values.name}
                    onChange={({ detail }) => {
                      handleNameChange(detail.value, values, setFieldValue);
                    }}
                  />
                </FormField>
              </ColumnLayout>
            </Container>

            {/* Purchase Orders */}
            {(!values.isInternal || values.purchaseOrder?.length > 0) && (
              <Container header={(<Header>Upload Purchase Order</Header>)}>
                <FileUploader
                  uploadPath={`orders/${newOrderRef.id}/purchaseOrder`}
                  uploadedFiles={values.purchaseOrder || []}
                  uploadErrors={errors.purchaseOrder || []}
                  onEmpty={() => setFieldValue('purchaseOrder', [])}
                  onUpload={(newFiles) => setFieldValue('purchaseOrder', newFiles)}
                  fileTypeName="Purchase Order"
                />
              </Container>
            )}

            {/* Shipments */}
            <FieldArray name="shipments">
              {({ remove, push }) => (
                <SpaceBetween direction="vertical" size="s">
                  {values.shipments.map((shipment, index) => (
                    <Container
                      // eslint-disable-next-line react/no-array-index-key
                      key={`shipment_${index}`}
                      header={(
                        <Header
                          actions={index !== 0 && (
                            <Button
                              type="button"
                              onClick={() => remove(index)}
                            >
                              Delete Shipment
                            </Button>
                          )}
                        >
                          {`Shipment ${index + 1}`}
                        </Header>
                      )}
                    >
                      <SpaceBetween direction="vertical" size="s">
                        {/* Shipment basic Info */}
                        <ColumnLayout columns={2}>
                          {!values.isInternal && (
                            <FormField
                              label="Name"
                              errorText={errors?.shipments?.[index]?.name}
                            >
                              <Input
                                name={`shipments[${index}].name`}
                                value={values.shipments[index]?.name}
                                onChange={({ detail }) => { setFieldValue(`shipments[${index}].name`, detail.value); }}
                              />
                            </FormField>
                          )}
                          <FormField
                            label="Target Ship Date"
                            errorText={errors?.shipments?.[index]?.targetShipDate}
                          >
                            <Input
                              name={`shipments[${index}].targetShipDate`}
                              value={values.shipments[index]?.targetShipDate}
                              onChange={({ detail }) => { setFieldValue(`shipments[${index}].targetShipDate`, detail.value); }}
                            />
                          </FormField>

                          {!values.isInternal && (
                            <>
                              <FormField
                                label="Address"
                                errorText={errors?.shipments?.[index]?.addressData ? 'Address is required' : ''}
                              >
                                <AddressDisplay
                                  backupAddress={values.shipments[index]?.address}
                                  address={values.shipments[index]?.addressData}
                                  onEdit={(newAddress) => {
                                    setFieldValue(`shipments[${index}].addressData`, newAddress);
                                  }}
                                />
                              </FormField>
                              <FormField
                                label={(values.subtotal || !isEditing) ? 'Shipment Subtotal' : 'Amount'}
                                errorText={errors?.shipments?.[index]?.amount}
                              >
                                <Input
                                  name={`shipments[${index}].amount`}
                                  value={values.shipments[index].amount}
                                  onChange={({ detail }) => { setFieldValue(`shipments[${index}].amount`, detail.value); }}
                                  onBlur={() => { setFieldValue(`shipments[${index}].amount`, formatCurrency(values.shipments[index].amount)); }}
                                />
                              </FormField>
                              <FormField
                                label="Shipping Method"
                                errorText={errors?.shipments?.[index]?.shippingMethod}
                              >
                                <Input
                                  name={`shipments[${index}].shippingMethod`}
                                  value={values.shipments[index]?.shippingMethod}
                                  onChange={({ detail }) => { setFieldValue(`shipments[${index}].shippingMethod`, detail.value); }}
                                />
                              </FormField>
                            </>
                          )}

                          {isEditing && (
                            <FormField
                              label="Shipped"
                              errorText={errors?.shipments?.[index]?.isShipped}
                            >
                              <Checkbox
                                onChange={({ detail }) => setFieldValue(`shipments[${index}].isShipped`, detail.checked)}
                                checked={values.shipments[index]?.isShipped}
                              >
                                Shipped
                              </Checkbox>
                            </FormField>
                          )}
                        </ColumnLayout>

                        {/* Package Table */}
                        {isEditing && !values.isInternal && (
                          <PackageTable
                            orderId={newOrderRef.id}
                            shipmentIdx={index}
                            trackingData={shipment.trackingData || []}
                          />
                        )}

                        {/* LineItems */}
                        <LineItemsTable
                          shipment={shipment}
                          shipmentIndex={index}
                          onNewLineItem={() => handleNewLineItem({
                            shipmentIndex: index,
                            setFieldValue,
                            currentLineItems: values.shipments[index].lineItems,
                          })}
                        />
                      </SpaceBetween>
                    </Container>
                  ))}
                  <Button
                    type="button"
                    formAction="none"
                    onClick={() => push({ ...defaultShipment })}
                  >
                    Add Shipment
                  </Button>
                </SpaceBetween>
              )}
            </FieldArray>

            {/* Submit Button */}
            <Box padding={{ vertical: 's' }} textAlign="right">
              <Button type="submit" variant="primary" disabled={isSubmitting || !dirty}>
                {isEditing ? 'Update Order' : 'Create Order'}
              </Button>
            </Box>
          </SpaceBetween>
        </Form>
      )}
    </Formik>
  );
}

ManageOrder.propTypes = {
  initData: PropTypes.shape({
    uid: PropTypes.string,
    name: PropTypes.string,
    orderDate: PropTypes.string,
    status: PropTypes.string,
    stripeId: PropTypes.string,
    purchaseOrder: PropTypes.arrayOf(PropTypes.shape({
      downloadURL: PropTypes.string,
      lastModifiedTS: PropTypes.number,
      name: PropTypes.string,
      ref: PropTypes.string,
      type: PropTypes.string,
    })),
    shipments: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      targetShipDate: PropTypes.string,
      shippingMethod: PropTypes.string,
      lineItems: PropTypes.arrayOf(PropTypes.shape({
        projectId: PropTypes.string,
        quantity: PropTypes.number,
        bonding: PropTypes.string,
        notes: PropTypes.string,
        steps: PropTypes.arrayOf(PropTypes.shape({
          type: PropTypes.string,
          goal: PropTypes.number,
          name: PropTypes.string,
        })),
      })),
      amount: PropTypes.string,
    })),
  }),
  isEditing: PropTypes.bool,
};
